import React, { useEffect, useState } from "react";
import { api } from "../lib/nurseryApi";
import { Camera, Package, ChatCircleDots, Clock, TrendUp, Leaf, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, sub, variant }) => (
  <div className={`stat-tile ${variant === "primary" ? "accent" : variant === "warm" ? "warm" : ""}`}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.85 }}>{label}</div>
      <div style={{ width: 38, height: 38, borderRadius: 12, background: variant ? "rgba(255,255,255,0.18)" : "var(--primary-soft)", display: "grid", placeItems: "center" }}>
        <Icon size={20} weight="fill" color={variant ? "white" : "var(--primary)"} />
      </div>
    </div>
    <div className="font-display" style={{ fontSize: 48, marginTop: 14, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12.5, marginTop: 8, opacity: 0.85 }}>{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  const runForecast = async () => {
    setForecastLoading(true);
    try {
      const { data } = await api.get("/inventory/forecast");
      setForecast(data);
    } catch (e) {
      setForecast({ summary: "Forecast unavailable.", forecast: [] });
    } finally {
      setForecastLoading(false);
    }
  };

  return (
    <div data-testid="dashboard-page" className="fade-up">
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Field Notes</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>This week's report</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 8, marginBottom: 28, fontSize: 15 }}>Quick numbers from your nursery — updated live.</p>

      {!stats ? (
        <div className="card" style={{ padding: 24 }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <Stat icon={Clock} label="Labor hours saved" value={stats.labor_hours_saved} sub="vs manual scouting + counting" variant="primary" />
          <Stat icon={Leaf} label="Plants scanned" value={stats.plants_scanned} sub="all-time diagnoses" />
          <Stat icon={Package} label="Inventory value" value={`$${Math.round(stats.inventory_value).toLocaleString()}`} sub={`${stats.inventory_items} varieties tracked`} />
          <Stat icon={TrendUp} label="Shrinkage prevented" value={`$${Math.round(stats.shrinkage_prevented_estimate).toLocaleString()}`} sub="AI early-detection estimate" variant="warm" />
          <Stat icon={ChatCircleDots} label="Leads captured" value={stats.leads_captured} sub="from chatbot + forms" />
        </div>
      )}

      <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 24 }} className="dash-grid">
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--secondary)" }}>AI Demand Forecast</div>
              <h2 className="font-display" style={{ fontSize: 28, marginTop: 6 }}>Next 30 days</h2>
            </div>
            <button className="btn btn-primary" onClick={runForecast} disabled={forecastLoading} data-testid="run-forecast-btn">
              <Sparkle size={14} weight="fill" /> {forecastLoading ? "Thinking…" : "Run forecast"}
            </button>
          </div>
          {forecast ? (
            <div>
              <p style={{ marginBottom: 14, fontSize: 15, lineHeight: 1.55, color: "var(--text-soft)" }}>{forecast.summary}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(forecast.forecast || []).map((f, i) => (
                  <div key={i} style={{ background: "var(--bg-alt)", borderRadius: 14, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700 }}>{f.variety}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span className={`badge ${f.next_30_days_demand === "high" ? "badge-success" : f.next_30_days_demand === "low" ? "badge-danger" : "badge-warn"}`}>{f.next_30_days_demand}</span>
                      <span className="badge">{f.action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: 24, background: "var(--bg-alt)", borderRadius: 14, color: "var(--text-soft)", fontSize: 14.5 }}>
              Click "Run forecast" to ask the AI what's selling next month, what to reorder, and what to mark down.
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 24, background: "linear-gradient(160deg, var(--primary), #3F5640)", color: "white", border: "none" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent)" }}>Quick actions</div>
          <h2 className="font-display" style={{ fontSize: 28, marginTop: 6, color: "white" }}>Get to work</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 22 }}>
            <Link to="/app/scanner" className="btn" style={{ background: "var(--secondary)", color: "white", justifyContent: "flex-start" }} data-testid="quick-scan-btn">
              <Camera size={16} weight="bold" /> Scan a sick plant
            </Link>
            <Link to="/app/inventory" className="btn" style={{ background: "rgba(255,255,255,0.94)", color: "var(--text)", justifyContent: "flex-start" }} data-testid="quick-inv-btn">
              <Package size={16} weight="bold" /> Update inventory
            </Link>
            <Link to="/app/chatbot" className="btn" style={{ background: "rgba(255,255,255,0.94)", color: "var(--text)", justifyContent: "flex-start" }} data-testid="quick-chat-btn">
              <ChatCircleDots size={16} weight="bold" /> Try the chatbot
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .dash-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}
